import { resolveCanonicalSymbol } from "../utils/coinSymbols";

const ENGINE_ALIASES = {
  dream: "dream", signal: "dream", signal_engine: "dream",
  horizon: "horizon", nebula_horizon: "horizon",
  scalp: "scalp", scalping: "scalp", scalping_engine: "scalp",
};

export function normalizeGaugeEngine(value) {
  const key = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ENGINE_ALIASES[key] || null;
}

export function normalizeGaugeSymbol(value) {
  return resolveCanonicalSymbol(value);
}

export function gaugeKey(engine, symbol) {
  const normalizedEngine = normalizeGaugeEngine(engine);
  const normalizedSymbol = normalizeGaugeSymbol(symbol);
  return normalizedEngine && normalizedSymbol ? `${normalizedEngine}:${normalizedSymbol}` : null;
}

const MODE_EVIDENCE_SOURCES = [
  { key: "runtimeMode", priority: 0, read: (source) => source?.runtimeMode?.mode },
  { key: "tradingModeStatus", priority: 1, read: (source) => source?.tradingModeStatus?.effective_execution_mode ?? source?.tradingModeStatus?.execution_mode },
  { key: "wallet", priority: 2, read: (source) => source?.wallet?.effective_execution_mode ?? source?.wallet?.execution_mode },
];

// Mode observations are deliberately UTC-Z only. Numeric timestamps and
// timezone-offset strings are not accepted because their semantics are too
// easy to mix with the API's UTC timestamp contract.
const UTC_ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

export function isStrictUtcIsoTimestamp(value) {
  if (typeof value !== "string" || !UTC_ISO_TIMESTAMP.test(value)) return false;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return false;
  const canonical = value.includes(".") ? value : value.replace("Z", ".000Z");
  return new Date(timestamp).toISOString() === canonical;
}

function utcTimestampMs(value) {
  return isStrictUtcIsoTimestamp(value) ? Date.parse(value) : null;
}

function hasModeValue(value) {
  return value !== null && value !== undefined && value !== "";
}

/**
 * Resolve mode evidence collected from the canonical runtime-mode endpoints.
 *
 * A successful dashboard poll records its UTC-Z client observation time in
 * `modeEvidenceObservedAt`. Evidence with a valid timestamp wins by newest
 * observation. Equal timestamps use the stable authority order runtime mode,
 * trading-mode status, then wallet. A current successful runtime-mode response
 * is therefore authoritative whenever it is at least as fresh as a fallback.
 *
 * Legacy callers may omit `modeEvidenceObservedAt`. In that explicit
 * compatibility mode there is no freshness comparison, so the same stable
 * authority order is used. Invalid or non-UTC-Z timestamps are treated as
 * timestamp-less evidence and can never outrank valid timestamped evidence.
 */
export function resolveGaugeModeEvidence(source = {}) {
  const observedAt = source?.modeEvidenceObservedAt || {};
  const candidates = MODE_EVIDENCE_SOURCES
    .map((descriptor) => {
      const raw = descriptor.read(source);
      if (!hasModeValue(raw)) return null;
      const timestamp = utcTimestampMs(observedAt?.[descriptor.key]);
      return {
        ...descriptor,
        raw,
        observedAt: timestamp === null ? null : observedAt[descriptor.key],
        timestamp,
        mode: /live/i.test(String(raw)) ? "LIVE" : "PAPER",
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftTimestamped = left.timestamp !== null;
      const rightTimestamped = right.timestamp !== null;
      if (leftTimestamped !== rightTimestamped) return leftTimestamped ? -1 : 1;
      if (leftTimestamped && left.timestamp !== right.timestamp) return right.timestamp - left.timestamp;
      return left.priority - right.priority;
    });

  return candidates[0] || { key: null, raw: null, observedAt: null, timestamp: null, mode: "PAPER" };
}

export function gaugeMode(source = {}) {
  return resolveGaugeModeEvidence(source).mode;
}

const finite = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const first = (source, keys) => {
  for (const key of keys) {
    if (source && source[key] !== null && source[key] !== undefined && source[key] !== "") return source[key];
  }
  return null;
};

const firstFinite = (source, keys) => {
  for (const key of keys) {
    const value = finite(source?.[key]);
    if (value !== null) return value;
  }
  return null;
};

const hasNonzeroQuantity = (qty) => qty !== null && qty !== 0;

function positionIsOpen(position, qty) {
  if (!position || qty === 0) return false;
  const status = String(position.status || "").trim().toUpperCase();
  if (["CLOSED", "EXITED", "FLAT", "NONE"].includes(status)) return false;
  // Strategy status is useful when the quantity is temporarily unavailable, but
  // a supplied zero must always win over an OPEN marker.
  return status === "OPEN" || hasNonzeroQuantity(qty);
}

function positionSide(position, qty) {
  const side = String(first(position, ["side", "position_side", "direction"]) || "").trim().toLowerCase();
  if (["short", "sell"].includes(side)) return -1;
  if (["long", "buy"].includes(side)) return 1;
  // Horizon's persisted state records an explicit directional status rather
  // than a separate side field. A signed quantity is likewise directional.
  const status = String(position?.status || "").trim().toUpperCase();
  if (status.startsWith("SHORT")) return -1;
  if (status.startsWith("LONG")) return 1;
  if (qty !== null && qty < 0) return -1;
  return null;
}

function positionMarkPrice(position, price) {
  // current_price is emitted by Horizon's active-position state. mark_price is
  // supported for exchange-style rows. A generic `price` is deliberately not
  // used: in Nebula state it can be an entry/fill price rather than a mark.
  return firstFinite(position, ["mark_price", "markPrice", "current_price", "currentPrice"]) ?? price;
}

function positionPnl(position, { open, qty, entryPrice, price }) {
  if (!open) return { value: null, kind: "unavailable" };

  // These are the only position fields that semantically mean *unrealized
  // percentage* PnL. In particular, pnl_pct/roi and realized_* are omitted:
  // those names are used by completed-trade data and may be schema defaults.
  const explicitUnrealized = firstFinite(position, ["unrealized_pnl_pct", "unrealized_return_pct"]);
  if (explicitUnrealized !== null) return { value: explicitUnrealized, kind: "unrealized" };

  // No fee estimate is applied here. A backend-supplied unrealized value is
  // authoritative; otherwise this is price movement only, avoiding invented
  // or double-counted fees.
  const side = positionSide(position, qty);
  if (!hasNonzeroQuantity(qty) || entryPrice === null || entryPrice <= 0 || price === null || side === null) {
    return { value: null, kind: "unavailable" };
  }
  // CoinGauge renders PnL as a percentage and authoritative fields above are
  // *_pct values. Therefore derived PnL has the same percentage unit. The
  // raw USD rule, (mark - entry) * quantity (reversed for shorts), belongs to
  // the backend's realized-PnL accounting and must not be mixed into this UI
  // percentage field.
  return {
    value: side * ((price - entryPrice) / entryPrice) * 100,
    kind: "unrealized_derived",
  };
}

/**
 * Resolve a current price from the dashboard price-hook contract.
 *
 * `source.prices` is a flat map returned by usePrices: its keys may be a
 * canonical symbol (BTC), a supported market alias (BTCUSDT), or a supported
 * coin-name alias (bitcoin); its values are finite numeric prices. Prices are
 * market-wide, not per-engine, but normalize the engine first so malformed
 * gauge identities never look up a quote. Nested engine maps and compound
 * `engine:symbol` keys are not part of this adapter contract.
 */
export function lookupGaugePrice(rawPrices, engine, symbol) {
  const normalizedEngine = normalizeGaugeEngine(engine);
  const normalizedSymbol = normalizeGaugeSymbol(symbol);
  if (!normalizedEngine || !normalizedSymbol || !rawPrices || typeof rawPrices !== "object" || Array.isArray(rawPrices)) return null;

  for (const [rawSymbol, rawPrice] of Object.entries(rawPrices)) {
    if (normalizeGaugeSymbol(rawSymbol) !== normalizedSymbol) continue;
    const price = finite(rawPrice);
    if (price !== null) return price;
  }
  return null;
}

const LIVE_ORIGIN_MARKERS = ["live", "exchange"];
const PAPER_ORIGIN_MARKERS = ["paper", "simulation"];

function positionOriginProvenance(position) {
  // state_origin is the persisted position contract. execution_mode is kept as
  // a compatibility fallback for older strategy-health payloads.
  const origin = String(position?.state_origin ?? position?.execution_mode ?? "").trim().toLowerCase();
  const identifiesLive = LIVE_ORIGIN_MARKERS.some((marker) => origin.includes(marker));
  const identifiesPaper = PAPER_ORIGIN_MARKERS.some((marker) => origin.includes(marker));
  if (identifiesLive && identifiesPaper) return "ambiguous";
  if (identifiesLive) return "live";
  if (identifiesPaper) return "paper";
  return "legacy";
}

function positionMatchesGaugeMode(position, mode) {
  const provenance = positionOriginProvenance(position);
  // LIVE gauges require affirmative live or exchange provenance. PAPER retains
  // originless legacy compatibility, but never displays live/exchange rows.
  if (mode === "LIVE") return provenance === "live";
  return provenance !== "live" && provenance !== "ambiguous";
}

function positionsByKey(strategyHealth, mode) {
  const output = new Map();
  const fields = [["dream_positions", "dream"], ["horizon_positions", "horizon"], ["scalping_positions", "scalp"]];
  for (const [field, engine] of fields) {
    for (const position of Array.isArray(strategyHealth?.[field]) ? strategyHealth[field] : []) {
      const key = gaugeKey(engine, position?.symbol || position?.coin || position?.canonical_coin || position?.market_symbol);
      if (!key) continue;
      if (!positionMatchesGaugeMode(position, mode)) continue;
      output.set(key, position);
    }
  }
  return output;
}

function walletPosition(wallet, engine, symbol, mode) {
  if (mode === "LIVE") return null;
  const bucket = wallet?.balances?.[engine];
  if (!bucket || typeof bucket !== "object") return null;
  for (const [rawSymbol, balance] of Object.entries(bucket)) {
    if (normalizeGaugeSymbol(rawSymbol) !== symbol) continue;
    const amount = finite(balance?.position ?? balance?.qty ?? balance?.quantity);
    if (amount === null) continue;
    return { qty: amount, source: "wallet" };
  }
  return null;
}

function healthForEngine(status, engine) {
  const modules = Array.isArray(status?.modules) ? status.modules : [];
  const expected = { dream: ["signal_engine", "dream"], horizon: ["nebula_horizon", "horizon"], scalp: ["scalping_engine", "scalp"] }[engine] || [];
  const row = modules.find((module) => expected.includes(String(module?.module || module?.name || "").toLowerCase()));
  const freshness = String(row?.freshness || row?.status || "unknown").toLowerCase();
  return { freshness, updatedAt: row?.latest_heartbeat_timestamp || row?.last_heartbeat_timestamp || null };
}

function cleanPerformanceByKey(tradeSummary) {
  const readiness = tradeSummary?.clean_measurement_readiness;
  const scope = tradeSummary?.performance_scope;
  const cleanMetrics = scope?.clean_metrics;
  if (
    scope?.scope !== "clean_post_quality_epoch_exact_closes"
    || scope?.headline_metrics_are_clean !== true
    || readiness?.clean_measurement_ready !== true
    || cleanMetrics?.scope !== "clean_post_quality_epoch_exact_closes"
    || !Number.isFinite(Number(cleanMetrics?.closed_trade_count))
    || Number(cleanMetrics.closed_trade_count) <= 0
  ) return new Map();
  const result = new Map();
  for (const sample of Array.isArray(tradeSummary?.samples) ? tradeSummary.samples : []) {
    const key = gaugeKey(sample?.engine, sample?.canonical_coin || sample?.coin || sample?.symbol);
    const value = finite(sample?.return_pct);
    if (!key || value === null) continue;
    const previous = result.get(key) || { total: 0, count: 0 };
    result.set(key, { total: previous.total + value, count: previous.count + 1 });
  }
  return new Map([...result].map(([key, value]) => [key, value.total / value.count]));
}

function sourceSignalByKey(source) {
  const output = new Map();
  const candidates = [
    ...(Array.isArray(source?.thinker?.coins) ? source.thinker.coins : []),
    ...(Array.isArray(source?.thinker?.insights) ? source.thinker.insights : []),
    ...(Array.isArray(source?.strategyScores?.scores) ? source.strategyScores.scores : []),
  ];
  for (const row of candidates) {
    const key = gaugeKey(row?.engine || row?.module, row?.symbol || row?.coin || row?.canonical_coin);
    if (!key) continue;
    output.set(key, {
      direction: first(row, ["direction", "trend", "signal_direction", "side"]),
      confidence: finite(first(row, ["confidence", "mcp_confidence", "signal_strength", "score"])),
    });
  }
  return output;
}

function observedAtForModeSource(options, sourceKey) {
  const supplied = options?.sourceObservedAt?.[sourceKey] ?? options?.observedAt;
  if (supplied !== undefined) return isStrictUtcIsoTimestamp(supplied) ? supplied : null;
  // toISOString() is always a strict UTC-Z timestamp. It records observation
  // time only for an actually supplied successful source below.
  return new Date().toISOString();
}

function mergeModeEvidenceObservedAt(previous, updates, options) {
  const merged = {};
  for (const descriptor of MODE_EVIDENCE_SOURCES) {
    const prior = previous?.modeEvidenceObservedAt?.[descriptor.key];
    if (isStrictUtcIsoTimestamp(prior)) merged[descriptor.key] = prior;
    if (!Object.prototype.hasOwnProperty.call(updates, descriptor.key)) continue;

    // A successful response without a usable mode replaces rather than
    // refreshes prior evidence. A failed response is represented by undefined
    // and never reaches this function, leaving its old observation visibly old.
    if (hasModeValue(descriptor.read({ [descriptor.key]: updates[descriptor.key] }))) {
      const observedAt = observedAtForModeSource(options, descriptor.key);
      if (observedAt) merged[descriptor.key] = observedAt;
      else delete merged[descriptor.key];
    } else {
      delete merged[descriptor.key];
    }
  }
  return merged;
}

function modeOnlyWalletEvidence(wallet) {
  if (!wallet || typeof wallet !== "object" || Array.isArray(wallet)) return wallet;
  const modeOnly = {};
  for (const field of ["effective_execution_mode", "execution_mode", "live_exchange_authoritative"]) {
    if (Object.prototype.hasOwnProperty.call(wallet, field)) modeOnly[field] = wallet[field];
  }
  return modeOnly;
}

export function mergeGaugeSources(previous = {}, updates = {}, options = {}) {
  // Failed endpoints are represented as undefined by the poller. Do not let a
  // single failed source erase independently refreshed valid telemetry.
  const definedUpdates = Object.fromEntries(Object.entries(updates).filter(([, value]) => value !== undefined));
  const previousMode = gaugeMode(previous);
  const modeEvidenceObservedAt = mergeModeEvidenceObservedAt(previous, definedUpdates, options);
  const next = { ...previous, ...definedUpdates, modeEvidenceObservedAt };
  const nextMode = gaugeMode(next);
  if (previousMode !== nextMode) {
    // Position, wallet balances, and clean-performance rows are mode-specific.
    // Never carry either the old values or a concurrently refreshed but
    // potentially pre-switch snapshot across a resolved mode change. Keep only
    // wallet fields that are mode evidence; the next successful wallet poll can
    // repopulate eligible PAPER position rows.
    const cleared = { ...next };
    delete cleared.strategyHealth;
    delete cleared.tradeSummary;
    if (Object.prototype.hasOwnProperty.call(definedUpdates, "wallet")) {
      cleared.wallet = modeOnlyWalletEvidence(definedUpdates.wallet);
    } else {
      delete cleared.wallet;
    }
    return cleared;
  }
  return next;
}

export function buildGaugeTelemetry(source = {}) {
  const mode = gaugeMode(source);
  const rows = new Map();
  const engineCoins = source?.engineCoins || {};
  Object.entries(engineCoins).forEach(([rawEngine, symbols]) => {
    const engine = normalizeGaugeEngine(rawEngine);
    if (!engine) return;
    (Array.isArray(symbols) ? symbols : []).forEach((rawSymbol) => {
      const symbol = normalizeGaugeSymbol(rawSymbol);
      const key = gaugeKey(engine, symbol);
      if (key) rows.set(key, { key, engine, symbol, mode, status: "loading" });
    });
  });
  const positions = positionsByKey(source.strategyHealth, mode);
  const signals = sourceSignalByKey(source);
  const performance = cleanPerformanceByKey(source.tradeSummary);
  const liveAvailable = source?.wallet?.live_exchange_authoritative === true;
  return [...rows.values()].map((row) => {
    const position = positions.get(row.key) || walletPosition(source.wallet, row.engine === "scalp" ? "scalping" : row.engine, row.symbol, mode);
    const qty = finite(position?.qty ?? position?.quantity ?? position?.size);
    const entryPrice = firstFinite(position, ["entry_price", "entryPrice", "average_entry_price", "avg_entry_price"]);
    const price = positionMarkPrice(position, lookupGaugePrice(source?.prices, row.engine, row.symbol));
    const health = healthForEngine(source.status, row.engine);
    const signal = signals.get(row.key) || {};
    const performancePct = performance.get(row.key) ?? null;
    const open = positionIsOpen(position, qty);
    const pnl = positionPnl(position, { open, qty, entryPrice, price });
    return {
      ...row, price, updatedAt: source.priceUpdatedAt ?? (health.updatedAt && Number.isFinite(Date.parse(health.updatedAt)) ? Date.parse(health.updatedAt) : null),
      freshness: health.freshness, signalDirection: signal.direction ?? null, confidence: signal.confidence ?? null,
      positionState: mode === "LIVE" && !liveAvailable && !position ? "unavailable" : (open ? "open" : "flat"),
      positionUnavailableReason: mode === "LIVE" && !liveAvailable && !position ? "live_open_position_data_unavailable" : null,
      positionQty: qty, pnl: pnl.value, pnlKind: pnl.kind,
      performancePct, performanceEligible: performancePct !== null,
    };
  });
}
